import { useContext, useMemo } from 'react';
import { ConfiguratorContext } from '@/components/Configurator/ConfiguratorContext';
import { buildSlotDisplayContext, type ComponentProductTypeResolver, type DisplayContext } from '@canvas-logic/clp-localization';
import { useDisplayContext } from '@canvas-logic/clp-shared-ui';

/**
 * Builds the DisplayContext that should be active for a slot's subtree —
 * the slot's `cmpKey` plus the filler PT (`bptKey` for basic, `cptKey` for composite).
 *
 * Both `<ComponentElement>` and `<ComponentItemsContainer>` need this same
 * context: ComponentElement to scope products under the component, and the
 * container to scope sibling attributes (ADR-0045 gap #2).
 */
export function useSlotDisplayContext(componentKey: string | undefined): DisplayContext {
  const parentDisplayCtx = useDisplayContext();
  const cfg = useContext(ConfiguratorContext);
  const api = cfg?.sdk?.api as ComponentProductTypeResolver | undefined;
  const filler = componentKey ? api?.getComponentProductType?.(componentKey) : undefined;
  const nestedProductKey =
    componentKey && filler?.isComposite ? api?.getComponentProductData?.(componentKey)?.productKey : undefined;

  return useMemo<DisplayContext>(
    () => buildSlotDisplayContext(parentDisplayCtx, { api, componentKey }),
    [parentDisplayCtx, api, componentKey, filler?.key, filler?.isComposite, nestedProductKey],
  );
}
